import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Stars } from "@/components/Stars";
import { useRecipeNote } from "@/lib/favorites";
import { useLang } from "@/context/LanguageContext";
import { NotebookPen, Lock, Trash2, RefreshCw } from "lucide-react";

// "Notes maison": the household's own rating + short tweak note for a recipe (Plus/Premium)
export function RecipeNotes({ recipeId, note }) {
  const { t } = useLang();
  const { paid, save, remove, busy } = useRecipeNote(recipeId);
  const [rating, setRating] = useState(note?.rating ?? null);
  const [text, setText] = useState(note?.note || "");
  useEffect(() => { setRating(note?.rating ?? null); setText(note?.note || ""); }, [note]);

  const dirty = rating !== (note?.rating ?? null) || text.trim() !== (note?.note || "");
  const canSave = paid && dirty && (rating || text.trim());

  return (
    <div className="chenia-card p-6" data-testid="recipe-notes">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="text-xl font-semibold inline-flex items-center gap-2"><NotebookPen className="h-5 w-5 text-primary" /> {t("household_notes")}</h2>
        {note?.updated_at && <span className="text-xs text-muted-foreground" data-testid="recipe-note-date">{new Date(note.updated_at).toLocaleDateString()}</span>}
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t("household_notes_sub")}</p>

      {!paid ? (
        <div className="rounded-2xl bg-chenia-rosesoft/50 p-4 flex flex-wrap items-center gap-3" data-testid="recipe-notes-locked">
          <Lock className="h-4 w-4 text-primary" />
          <p className="text-sm flex-1 min-w-40">{t("premium_feature")}</p>
          <Button asChild size="sm" className="rounded-full bg-primary hover:bg-chenia-terracottadark"><Link to="/pricing" data-testid="recipe-notes-unlock">{t("unlock")}</Link></Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">{t("your_rating")}</p>
            <Stars value={rating || 0} onChange={setRating} size="lg" testid="note-stars" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">{t("your_note")}</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} placeholder={t("note_placeholder")} rows={3}
              className="rounded-2xl resize-none" data-testid="note-textarea" />
            <p className="text-right text-[11px] text-muted-foreground mt-1">{text.length}/300</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => save({ rating, note: text.trim() })} disabled={!canSave || busy} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="note-save">
              {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : t("save_note")}
            </Button>
            {note && (
              <Button variant="ghost" onClick={remove} disabled={busy} className="rounded-full text-muted-foreground hover:text-destructive" data-testid="note-delete">
                <Trash2 className="h-4 w-4 mr-1" /> {t("delete_note")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
